import { npcUsernameForSeed } from "@/lib/npcDisplayNames";

/** Difficulty labels stored per bot (matchmaking flavor only). */
export type BotDifficultyLabel = "easy" | "medium" | "hard" | "expert";

/** Shipped `/public/profile-bots/*.webp` (01–10 stock, 11–30 Cursor-generated) — cycled across 200 bots. */
const PROFILE_BOT_AVATAR_COUNT = 30;
export const OFFLINE_BOT_AVATAR_PATHS = Array.from(
  { length: PROFILE_BOT_AVATAR_COUNT },
  (_, i) => `/profile-bots/${String(i + 1).padStart(2, "0")}.webp`,
) as readonly string[];

/**
 * Offline NPC portraits share the same stock set as player fallbacks; pick another path if they collide.
 */
export function rivalAvatarDistinctFromPlayer(
  npcAvatarUrl: string,
  playerPortraitUrl: string,
): string {
  const avoid = playerPortraitUrl.trim();
  const cur = npcAvatarUrl.trim();
  if (!avoid || cur !== avoid) return npcAvatarUrl;
  const alt = OFFLINE_BOT_AVATAR_PATHS.find((p) => p !== avoid);
  return alt ?? npcAvatarUrl;
}

export interface OfflineBot {
  id: string;
  displayName: string;
  difficulty: BotDifficultyLabel;
  /** Trophies-style label for matchmaking UI */
  tierLabel: string;
  /** Fake score for matchmaking / results flavor */
  score: number;
  /** Local `/public` path for Next/Image */
  avatarUrl: string;
}

/** League tier strings cycled for variety */
const TIER_ROTATION = [
  "Bronze III",
  "Bronze II",
  "Bronze I",
  "Silver III",
  "Silver II",
  "Silver I",
  "Gold III",
  "Gold II",
  "Gold I",
] as const;

/** 90 easy, 55 medium, 30 hard, 25 expert = 200 */
const DIFFICULTY_SEQUENCE: BotDifficultyLabel[] = [
  ...Array.from({ length: 90 }, () => "easy" as const),
  ...Array.from({ length: 55 }, () => "medium" as const),
  ...Array.from({ length: 30 }, () => "hard" as const),
  ...Array.from({ length: 25 }, () => "expert" as const),
];

function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

function avatarPathForBotIndex(i: number): string {
  return OFFLINE_BOT_AVATAR_PATHS[i % OFFLINE_BOT_AVATAR_PATHS.length]!;
}

function buildRoster(): OfflineBot[] {
  const tiers = [...DIFFICULTY_SEQUENCE];
  shuffleInPlace(tiers);

  const roster: OfflineBot[] = [];
  for (let i = 0; i < 200; i++) {
    const id = `offline-bot-${String(i).padStart(3, "0")}`;
    const displayName = npcUsernameForSeed(id);
    const tierLabel = TIER_ROTATION[i % TIER_ROTATION.length];
    const score = 420 + ((i * 47 + 13) % 2800);
    roster.push({
      id,
      displayName,
      difficulty: tiers[i]!,
      tierLabel,
      score,
      avatarUrl: avatarPathForBotIndex(i),
    });
  }
  return roster;
}

/** All 200 simulated opponents (offline). */
export const OFFLINE_BOTS: readonly OfflineBot[] = buildRoster();

export function pickRandomOfflineBot(): OfflineBot {
  const idx = Math.floor(Math.random() * OFFLINE_BOTS.length);
  return OFFLINE_BOTS[idx]!;
}

const EASY_BOTS = OFFLINE_BOTS.filter((b) => b.difficulty === "easy");

/** Guaranteed easy rival for first-time duel (FTUE). */
export function pickEasyOfflineBot(): OfflineBot {
  if (EASY_BOTS.length === 0) return OFFLINE_BOTS[0]!;
  const idx = Math.floor(Math.random() * EASY_BOTS.length);
  return EASY_BOTS[idx]!;
}

